// Recarga na barra de ações: um "relógio" escuro que vai abrindo conforme a magia, runa ou poção recarrega,
// com os segundos que faltam (como no cliente do Tibia). O servidor manda o tempo restante de cada recarga.
import { useEffect, useRef } from 'preact/hooks';
import type { HotbarSlot } from '../../../../shared/src/game/helper';
import { potionOf } from '../../../../shared/src/game/combat';
import { runeByItem, spellByWords } from '../spells';
import { catalogNow } from '../items';

const cds = new Map<string, { until: number; total: number }>();

/** mensagem 'cds' do servidor: [chave, ms restantes] */
export function setCooldowns(list: Array<[string, number]>) {
  const now = performance.now();
  const seen = new Set<string>();
  for (const [k, ms] of list) {
    seen.add(k);
    const until = now + ms, old = cds.get(k);
    // recarga nova (ou renovada): o total é o tempo restante agora; a mesma recarga mantém o total de quando começou
    if (!old || until > old.until + 150 || old.until <= now) cds.set(k, { until, total: Math.max(ms, 1) });
    else old.until = until;
  }
  for (const k of [...cds.keys()]) if (!seen.has(k)) cds.delete(k);
}

const group = (g: string | undefined) => (g === 'healing' ? 'g:healing' : g === 'attack' ? 'g:attack' : 'g:support');

/** chaves de recarga do que está no espaço: a própria e a do grupo (ataque/cura/suporte) */
function keysOf(sl: HotbarSlot): string[] {
  if ('spell' in sl.use) { const s = spellByWords(sl.use.spell); return [sl.use.spell, 'heal:' + sl.use.spell, group(s?.group)]; }
  const r = runeByItem(sl.use.item);
  if (r) return ['r:' + sl.use.item, group(r.group)];
  const name = catalogNow()?.get(sl.use.item)?.name;
  return potionOf(name) ? ['potion'] : [];
}

/** a recarga que termina por último entre as chaves */
function current(keys: string[], now: number) {
  let best: { until: number; total: number } | null = null;
  for (const k of keys) { const c = cds.get(k); if (c && c.until > now && (!best || c.until > best.until)) best = c; }
  return best;
}

export function CdOverlay(props: { slot: HotbarSlot | null }) {
  const el = useRef<HTMLSpanElement>(null), txt = useRef<HTMLElement>(null);
  const sl = props.slot;
  useEffect(() => {
    if (!sl) return;
    const keys = keysOf(sl);
    let raf = 0;
    const draw = () => {
      const now = performance.now(), c = current(keys, now), e = el.current;
      if (e) {
        if (!c) { e.style.display = 'none'; }
        else {
          const left = c.until - now, pct = Math.max(0, Math.min(100, (left / c.total) * 100));
          e.style.display = '';
          e.style.background = `conic-gradient(rgba(0,0,0,.72) 0 ${pct}%, rgba(0,0,0,0) ${pct}% 100%)`;
          if (txt.current) txt.current.textContent = left >= 1000 ? String(Math.ceil(left / 1000)) : '';
        }
      }
      raf = requestAnimationFrame(draw);
    };
    raf = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(raf);
  }, [sl && JSON.stringify(sl.use)]);
  if (!sl) return null;
  return <span ref={el} class="ab-cd" style={{ display: 'none' }}><i ref={txt} /></span>;
}
